import React from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { formatDateTime } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Books, MagnifyingGlass } from "@phosphor-icons/react";
import { KNOWLEDGE_BASE } from "@/constants/testIds";
import KBDetailSheet from "@/components/knowledge-base/KBDetailSheet";
import KBUploadDialog from "@/components/knowledge-base/KBUploadDialog";
import {
	useKnowledgeBase,
	statusToLabel,
	statusToTone,
	formatBytes,
	DOCUMENT_STATUSES,
} from "@/lib/knowledgeBase";

/**
 * Phase 4F — Slice 1: Read-only Knowledge Base foundation.
 *
 * This page renders a workspace-scoped, read-only list of ingested documents.
 * All strings originate from the backend list response or from static
 * labels below. Titles and original filenames are rendered as escaped React
 * text nodes; no dangerouslySetInnerHTML is used anywhere. `unicode-bidi:
 * plaintext` is set on the two text-bearing cells so mixed Arabic/English
 * filenames render in their natural order.
 *
 * Later Phase 4F slices extended this foundation with detail, upload,
 * retry, delete, and bounded status polling.
 */

function StatusBadge({ status }) {
	return (
		<span
			data-testid={KNOWLEDGE_BASE.rowStatus}
			data-status={typeof status === "string" ? status : "unknown"}
			aria-label={`Status: ${statusToLabel(status)}`}
			className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${statusToTone(status)}`}
		>
			{statusToLabel(status)}
		</span>
	);
}

function formatDate(iso) {
	if (typeof iso !== "string" || iso.length === 0) return "—";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "—";
	return formatDateTime(d);
}

function DesktopTable({ items, onOpenDetail }) {
	return (
		<div
			data-testid={KNOWLEDGE_BASE.table}
			className="hidden md:block border border-black/10 rounded-md overflow-hidden"
		>
			<table className="w-full text-sm">
				<thead className="bg-[#F4F6F8] text-[#0B1F3A]">
					<tr className="text-left">
						<th className="px-4 py-2 font-medium">Title</th>
						<th className="px-4 py-2 font-medium">Filename</th>
						<th className="px-4 py-2 font-medium">Type</th>
						<th className="px-4 py-2 font-medium">Size</th>
						<th className="px-4 py-2 font-medium">Status</th>
						<th className="px-4 py-2 font-medium">Updated</th>
						<th className="px-4 py-2 font-medium sr-only">Actions</th>
					</tr>
				</thead>
				<tbody>
					{items.map((doc) => (
						<tr
							key={doc.document_id}
							data-testid={KNOWLEDGE_BASE.row}
							data-document-id={doc.document_id}
							className="border-t border-black/5 hover:bg-[#F9FAFB]"
						>
							<td
								className="px-4 py-2 text-[#0B1F3A]"
								style={{ unicodeBidi: "plaintext" }}
								data-testid={KNOWLEDGE_BASE.rowTitle}
							>
								{doc.title}
							</td>
							<td
								className="px-4 py-2 text-neutral-700 font-mono text-xs"
								style={{ unicodeBidi: "plaintext" }}
								data-testid={KNOWLEDGE_BASE.rowFilename}
							>
								{doc.original_filename}
							</td>
							<td className="px-4 py-2 text-neutral-600 text-xs" data-testid={KNOWLEDGE_BASE.rowMime}>
								{doc.mime_type}
							</td>
							<td className="px-4 py-2 text-neutral-700 tabular-nums" data-testid={KNOWLEDGE_BASE.rowSize}>
								{formatBytes(doc.size_bytes)}
							</td>
							<td className="px-4 py-2">
								<StatusBadge status={doc.status} />
							</td>
							<td className="px-4 py-2 text-neutral-500 text-xs" data-testid={KNOWLEDGE_BASE.rowUpdated}>
								{formatDate(doc.updated_at)}
							</td>
							<td className="px-4 py-2 text-right">
								<Button
									type="button"
									variant="secondary"
									className="h-7 px-2 text-xs"
									onClick={() => onOpenDetail(doc.document_id)}
									data-testid={KNOWLEDGE_BASE.viewDetailsButton}
									data-document-id={doc.document_id}
									aria-label={`View details for ${doc.title}`}
								>
									View details
								</Button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function MobileCards({ items, onOpenDetail }) {
	return (
		<div
			data-testid={KNOWLEDGE_BASE.cards}
			className="md:hidden space-y-2"
		>
			{items.map((doc) => (
				<div
					key={doc.document_id}
					data-testid={KNOWLEDGE_BASE.row}
					data-document-id={doc.document_id}
					className="border border-black/10 rounded-md p-3 bg-white"
				>
					<div
						className="text-sm font-medium text-[#0B1F3A]"
						style={{ unicodeBidi: "plaintext" }}
						data-testid={KNOWLEDGE_BASE.rowTitle}
					>
						{doc.title}
					</div>
					<div
						className="text-xs text-neutral-600 font-mono mt-0.5"
						style={{ unicodeBidi: "plaintext" }}
						data-testid={KNOWLEDGE_BASE.rowFilename}
					>
						{doc.original_filename}
					</div>
					<div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-neutral-600">
						<span data-testid={KNOWLEDGE_BASE.rowMime}>{doc.mime_type}</span>
						<span data-testid={KNOWLEDGE_BASE.rowSize}>{formatBytes(doc.size_bytes)}</span>
						<StatusBadge status={doc.status} />
						<span data-testid={KNOWLEDGE_BASE.rowUpdated} className="text-neutral-500">
							{formatDate(doc.updated_at)}
						</span>
					</div>
					<div className="mt-2">
						<Button
							type="button"
							variant="secondary"
							className="h-7 px-2 text-xs"
							onClick={() => onOpenDetail(doc.document_id)}
							data-testid={KNOWLEDGE_BASE.viewDetailsButton}
							data-document-id={doc.document_id}
							aria-label={`View details for ${doc.title}`}
						>
							View details
						</Button>
					</div>
				</div>
			))}
		</div>
	);
}

function LoadingState() {
	return (
		<div data-testid={KNOWLEDGE_BASE.loadingState} className="space-y-2">
			<Skeleton className="h-8 w-full" />
			<Skeleton className="h-8 w-full" />
			<Skeleton className="h-8 w-full" />
			<Skeleton className="h-8 w-full" />
			<Skeleton className="h-8 w-full" />
		</div>
	);
}

function EmptyState() {
	return (
		<div
			data-testid={KNOWLEDGE_BASE.emptyState}
			className="border border-dashed border-black/10 rounded-md py-12 text-center"
		>
			<Books size={32} weight="duotone" className="mx-auto text-[#0B4DCC] mb-2" />
			<div className="text-sm font-medium text-[#0B1F3A]">No documents yet</div>
			<p className="text-xs text-neutral-500 mt-1">
				Documents you upload to this workspace's Knowledge Base will appear here.
			</p>
		</div>
	);
}

function FilteredEmptyState() {
	return (
		<div
			data-testid={KNOWLEDGE_BASE.filteredEmptyState}
			className="border border-dashed border-black/10 rounded-md py-8 text-center text-sm text-neutral-500"
		>
			No documents match your search or filter.
		</div>
	);
}

function PermissionDeniedState() {
	return (
		<Alert
			data-testid={KNOWLEDGE_BASE.permissionState}
			variant="destructive"
			className="mt-2"
		>
			<AlertTitle>Access denied</AlertTitle>
			<AlertDescription>
				You do not have access to this workspace's Knowledge Base.
			</AlertDescription>
		</Alert>
	);
}

function NetworkErrorState({ onRetry }) {
	return (
		<Alert
			data-testid={KNOWLEDGE_BASE.networkErrorState}
			variant="destructive"
			className="mt-2"
		>
			<AlertTitle>Network error</AlertTitle>
			<AlertDescription className="space-y-2">
				<div>We couldn't reach the server. Please check your connection and try again.</div>
				<Button
					type="button"
					variant="secondary"
					className="h-8"
					onClick={onRetry}
					data-testid={KNOWLEDGE_BASE.retryButton}
				>
					Try again
				</Button>
			</AlertDescription>
		</Alert>
	);
}

function UnexpectedErrorState({ onRetry }) {
	return (
		<Alert
			data-testid={KNOWLEDGE_BASE.unexpectedErrorState}
			variant="destructive"
			className="mt-2"
		>
			<AlertTitle>Something went wrong</AlertTitle>
			<AlertDescription className="space-y-2">
				<div>We couldn't load your documents. Please try again.</div>
				<Button
					type="button"
					variant="secondary"
					className="h-8"
					onClick={onRetry}
					data-testid={KNOWLEDGE_BASE.retryButton}
				>
					Try again
				</Button>
			</AlertDescription>
		</Alert>
	);
}

export default function KnowledgeBasePage() {
	const kb = useKnowledgeBase();
	const {
		workspaceState,
		workspaceId,
		listState,
		documents,
		filtered,
		searchQuery,
		setSearchQuery,
		statusFilter,
		setStatusFilter,
		refetch,
	} = kb;

	const [selectedDocumentId, setSelectedDocumentId] = React.useState(null);
	const [isUploadOpen, setIsUploadOpen] = React.useState(false);

	const openDetail = React.useCallback((id) => {
		setSelectedDocumentId(id);
	}, []);
	const closeDetail = React.useCallback(() => {
		setSelectedDocumentId(null);
	}, []);
	const openUpload = React.useCallback(() => {
		setIsUploadOpen(true);
	}, []);
	const closeUpload = React.useCallback(() => {
		setIsUploadOpen(false);
	}, []);
	// Slice 4: on delete success, close the sheet and refetch exactly once.
	const handleDeleted = React.useCallback(() => {
		setSelectedDocumentId(null);
		return refetch();
	}, [refetch]);
	// Slice 5B: on a resolved retry attempt (200 or a non-catastrophic 409),
	// refetch the workspace list. The Sheet also refreshes its own detail
	// via the useDocumentDetail refetchDetail() trigger. The sheet remains
	// open so the user sees the freshest state without a jarring transition.
	const handleRetried = React.useCallback(() => {
		return refetch();
	}, [refetch]);

	const showControls =
		workspaceState === "ready" &&
		(listState === "ready" || listState === "loading") &&
		documents.length > 0;

	return (
		<AppShell
			subtitle="◆ KNOWLEDGE BASE · WORKSPACE SOURCES"
			title="Knowledge Base"
		>
			<div className="max-w-5xl mx-auto space-y-4" data-testid={KNOWLEDGE_BASE.pageRoot}>
				<div className="flex items-center justify-end">
					<Button
						type="button"
						onClick={openUpload}
						disabled={workspaceState !== "ready"}
						data-testid={KNOWLEDGE_BASE.uploadButton}
					>
						Upload document
					</Button>
				</div>
				<Alert data-testid={KNOWLEDGE_BASE.assistantHint} className="border-[#DCE3ED]">
					<AlertDescription className="text-sm text-[#0B1F3A]">
						Documents in <span className="font-medium">Ready</span> state are eligible for{" "}
						<Link
							to="/dashboard/assistant"
							className="text-[#0B4DCC] underline underline-offset-2"
							data-testid={KNOWLEDGE_BASE.assistantHintLink}
						>
							Knowledge Assistant
						</Link>{" "}
						retrieval.
					</AlertDescription>
				</Alert>

				{showControls && (
					<div className="flex flex-col sm:flex-row gap-2 sm:items-center">
						<div className="relative flex-1">
							<label htmlFor="kb-search" className="sr-only">
								Search documents by title or filename
							</label>
							<MagnifyingGlass
								size={14}
								weight="bold"
								className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
								aria-hidden="true"
							/>
							<Input
								id="kb-search"
								type="search"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search by title or filename…"
								className="pl-8 h-9"
								data-testid={KNOWLEDGE_BASE.searchInput}
							/>
						</div>
						<div>
							<label htmlFor="kb-status" className="sr-only">
								Filter by status
							</label>
							<select
								id="kb-status"
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
								data-testid={KNOWLEDGE_BASE.statusFilter}
								className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
							>
								<option value="all">All statuses</option>
								{DOCUMENT_STATUSES.map((s) => (
									<option key={s} value={s}>
										{statusToLabel(s)}
									</option>
								))}
							</select>
						</div>
					</div>
				)}

				{workspaceState === "loading" && <LoadingState />}

				{workspaceState === "unavailable" && (
					<Alert data-testid={KNOWLEDGE_BASE.unexpectedErrorState} variant="destructive" className="mt-2">
						<AlertTitle>Workspace unavailable</AlertTitle>
						<AlertDescription>
							We couldn't determine your workspace. Please refresh the page or contact support.
						</AlertDescription>
					</Alert>
				)}

				{workspaceState === "ready" && listState === "loading" && documents.length === 0 && (
					<LoadingState />
				)}

				{workspaceState === "ready" && listState === "permission_denied" && (
					<PermissionDeniedState />
				)}

				{workspaceState === "ready" && listState === "network_error" && (
					<NetworkErrorState onRetry={refetch} />
				)}

				{workspaceState === "ready" && listState === "unexpected_error" && (
					<UnexpectedErrorState onRetry={refetch} />
				)}

				{workspaceState === "ready" && listState === "ready" && documents.length === 0 && (
					<EmptyState />
				)}

				{workspaceState === "ready" && listState === "ready" && documents.length > 0 && (
					filtered.length === 0 ? (
						<FilteredEmptyState />
					) : (
						<>
							<DesktopTable items={filtered} onOpenDetail={openDetail} />
							<MobileCards items={filtered} onOpenDetail={openDetail} />
						</>
					)
				)}
			</div>
			<KBDetailSheet
				workspaceId={workspaceId}
				documentId={selectedDocumentId}
				isOpen={Boolean(selectedDocumentId)}
				onOpenChange={(open) => {
					if (!open) closeDetail();
				}}
				onDeleted={handleDeleted}
				onRetried={handleRetried}
			/>
			<KBUploadDialog
				workspaceId={workspaceId}
				isOpen={isUploadOpen}
				onClose={closeUpload}
				onUploaded={refetch}
			/>
		</AppShell>
	);
}
